import type { AccountRequirement, AccountRequirements } from '@identity/domain/types';

/**
 * Which step is pending: three pure functions over the step order and the requirement map. They
 * store nothing, so the step is derived again on every render.
 *
 * They live in presentation because all three answers are rendering decisions, and because a
 * requirement this build cannot render is a limit of the front end, not a fact about the account.
 *
 * @module @identity/presentation/utils/requirementProgress
 */

/**
 * First step of this flow that is required and still pending; `DONE` when none is.
 *
 * Only `false` counts as pending: a missing key means the requirement was not asked for, so it can
 * never block the wizard on a step that has no screen.
 */
export const nextStepFor = (
  order: readonly AccountRequirement[],
  requirements: AccountRequirements,
): AccountRequirement | 'DONE' => order.find((step) => requirements[step] === false) ?? 'DONE';

/**
 * Steps of this flow that are already met, for the sidebar checkmarks.
 *
 * It deliberately does not depend on the order: marking everything before the current step as done
 * would be a guess, and only what the backend reports as met is a fact.
 */
export const completedStepsFor = (
  order: readonly AccountRequirement[],
  requirements: AccountRequirements,
): AccountRequirement[] => order.filter((step) => requirements[step] === true);

/**
 * Requirement keys this build cannot render. A non-empty list means the wizard must stop: ignoring
 * one would finish the flow with an incomplete account.
 */
export const unsupportedRequirements = (
  order: readonly AccountRequirement[],
  requirements: AccountRequirements,
): string[] => Object.keys(requirements).filter((key) => !order.includes(key as AccountRequirement));
